
import { useEffect } from "react";
import { VacationCard } from "./VacationCard";
import { VacationCardSkeleton } from "./VacationCardSkeleton";
import { Button } from "@/components/ui/button";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { ScrollToTop } from "./ScrollToTop";

interface ResultsGridProps {
  displayedVacations: any[];
  filteredVacations: any[];
  displayCount: number;
  hasMoreResults: boolean;
  isFiltered: boolean;
  vacationCount: number;
  loading?: boolean;
  searchQuery?: string; // Add search query for tracking
  onDisplayCountChange: (count: number) => void;
  onResetFilters: () => void;
}

export const ResultsGrid = ({ 
  displayedVacations, 
  filteredVacations, 
  displayCount, 
  hasMoreResults, 
  isFiltered, 
  vacationCount, 
  loading = false,
  searchQuery,
  onDisplayCountChange, 
  onResetFilters 
}: ResultsGridProps) => {
  
  const { shouldLoadMore, resetLoadMore } = useInfiniteScroll({
    hasNextPage: hasMoreResults,
    isLoading: loading
  });

  useEffect(() => {
    if (shouldLoadMore) {
      const newCount = Math.min(displayCount + 20, filteredVacations.length);
      onDisplayCountChange(newCount);
      resetLoadMore();
    }
  }, [shouldLoadMore, displayCount, filteredVacations.length, onDisplayCountChange, resetLoadMore]);

  if (loading && displayedVacations.length === 0) {
    return (
      <>
        <div className="text-sm text-gray-600 flex items-center justify-between">
          <span>Vakanties laden...</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 9 }).map((_, index) => (
            <VacationCardSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="text-sm text-gray-600 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span>{displayedVacations.length} van {filteredVacations.length} {filteredVacations.length === 1 ? 'vakantie' : 'vakanties'} getoond</span>
          {isFiltered && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onResetFilters}
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
            >
              Filters weghalen
            </Button>
          )}
        </div>
        {vacationCount > 0 && (
          <span className="text-orange-600 font-medium">
            🎯 Zonnie's selectie voor jou
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayedVacations.map((vacation, index) => (
          <VacationCard
            key={vacation['product ID']}
            vacation={vacation}
            user={null}
            isFavorite={false}
            onFavoriteChange={() => {}}
            position={index}
            searchQuery={searchQuery}
          />
        ))}
        
        {loading && hasMoreResults && (
          Array.from({ length: 6 }).map((_, index) => (
            <VacationCardSkeleton key={`loading-skeleton-${index}`} />
          ))
        )}
      </div>

      {hasMoreResults && !loading && (
        <div className="text-center text-sm text-gray-500 py-4">
          Scroll naar beneden voor meer resultaten...
        </div>
      )}

      <ScrollToTop />
    </>
  );
};
